import { Controller } from "@hotwired/stimulus"
import * as smd from "streaming-markdown"

// Connects to data-controller="streaming-markdown"
export default class extends Controller {
    static targets = ["source", "output"]
    static values = { streamOngoing: Boolean }

    connect() {
        // Track that this controller is connected
        this.isConnected = true

        // Guard: ensure targets exist and element is in the DOM
        if (!this.hasOutputTarget || !this.hasSourceTarget) {
            console.warn("[streaming-markdown] Missing required targets, skipping initialization")
            return
        }

        // Guard: ensure the output target is connected to the document
        // If not connected yet, retry after the next animation frame
        if (!this.outputTarget.isConnected || !this.outputTarget.parentNode) {
            this.retryCount = (this.retryCount || 0) + 1
            if (this.retryCount < 5) {
                requestAnimationFrame(() => {
                    if (this.isConnected) this.connect()
                })
                return
            }
            console.warn("[streaming-markdown] Output target not connected to DOM after retries, skipping initialization")
            return
        }

        try {
            this.renderer = smd.default_renderer(this.outputTarget)

            // Override add_token to replace LINE_BREAK with space (soft break)
            // and guard against disconnected DOM
            const originalAddToken = this.renderer.add_token
            this.renderer.add_token = (data, type) => {
                // Guard: stop processing if controller has been disconnected
                if (!this.isConnected) return

                if (type === smd.LINE_BREAK) {
                    this.renderer.add_text(data, " ")
                    return
                }
                originalAddToken(data, type)
            }

            // Override set_attr to make all links open in new tab
            // and guard against disconnected DOM
            const originalSetAttr = this.renderer.set_attr
            this.renderer.set_attr = (data, type, value) => {
                // Guard: stop processing if controller has been disconnected
                if (!this.isConnected) return

                originalSetAttr(data, type, value)
                // When setting href on a link, also set target="_blank" for security
                if (type === smd.HREF) {
                    if (data.nodes && data.nodes[data.index]) {
                        data.nodes[data.index].setAttribute("target", "_blank")
                        data.nodes[data.index].setAttribute("rel", "noopener noreferrer")
                    }
                }
            }

            // Override add_text to guard against disconnected DOM
            const originalAddText = this.renderer.add_text
            this.renderer.add_text = (data, text) => {
                // Guard: stop processing if controller has been disconnected
                if (!this.isConnected) return
                // Guard: ensure parent node still exists
                if (!data.nodes || !data.nodes[data.index]) return

                originalAddText(data, text)
            }

            this.parser = smd.parser(this.renderer)

            // Process initial content
            if (this.hasSourceTarget && this.sourceTarget.textContent) {
                const content = this.sourceTarget.textContent

                if (!this.streamOngoingValue) {
                    // For static content (not streaming), feed content line by line
                    // to avoid issues with the streaming parser handling large chunks
                    const lines = content.split('\n')
                    for (let i = 0; i < lines.length; i++) {
                        if (!this.isConnected) break // Stop if disconnected
                        smd.parser_write(this.parser, lines[i])
                        // Add newline back except for last line
                        if (i < lines.length - 1) {
                            smd.parser_write(this.parser, '\n')
                        }
                    }
                    if (this.isConnected) {
                        smd.parser_end(this.parser)
                    }
                } else {
                    // For streaming content, write it all at once (more will come)
                    smd.parser_write(this.parser, content)
                }
            }

            // Watch for new content (only needed if streaming)
            if (this.streamOngoingValue) {
                this.observer = new MutationObserver(this.handleMutation.bind(this))
                this.observer.observe(this.sourceTarget, {
                    childList: true,
                    characterData: true,
                    subtree: true
                })

                // Also watch output for animation
                this.setupOutputObserver()
            }
        } catch (error) {
            console.error("[streaming-markdown] Error during initialization:", error)
        }
    }

    disconnect() {
        // Mark as disconnected immediately to stop any in-flight operations
        this.isConnected = false

        if (this.observer) {
            this.observer.disconnect()
        }
        if (this.outputObserver) {
            this.outputObserver.disconnect()
        }
        // Only end parser if streaming was ongoing (not already ended in connect)
        if (this.parser && this.streamOngoingValue) {
            try {
                smd.parser_end(this.parser)
            } catch (e) {
                // Parser may already be ended, ignore errors
            }
        }
        this.parser = null
        this.renderer = null
    }

    handleMutation(mutations) {
        // Guard: stop processing if controller has been disconnected
        if (!this.isConnected || !this.parser) return

        mutations.forEach((mutation) => {
            // Check again inside loop as disconnect could happen during iteration
            if (!this.isConnected || !this.parser) return

            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach((node) => {
                    if (!this.isConnected || !this.parser) return

                    if (node.nodeType === Node.TEXT_NODE) {
                        try {
                            smd.parser_write(this.parser, node.textContent)
                        } catch (e) {
                            // Only log if still connected (avoid noise during teardown)
                            if (this.isConnected) {
                                console.error("[streaming-markdown] Error writing to parser:", e)
                            }
                        }
                    }
                })
            }
        })
    }
    
    // We need to observe the output target to animate new nodes
    setupOutputObserver() {
        this.outputObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Add fade-in class to new elements
                            node.classList.add('animate-fade-in')
                        } else if (node.nodeType === Node.TEXT_NODE) {
                            // Text nodes don't support classes. 
                            // We might need to wrap them if they are direct children of output
                            // But usually they are inside elements (p, li, etc).
                            // If a text node is added to an existing element, we can't easily animate just that text
                            // without wrapping it.
                            
                            // For now, animating the block elements (p, ul, etc) is a good start.
                            // If we want word-by-word fade in, we'd need a custom renderer that wraps every word.
                        }
                    })
                }
            })
        })
        
        this.outputObserver.observe(this.outputTarget, {
            childList: true,
            subtree: true
        })
    }
}
